import { CMS_EVENT_DETAILS_STORAGE_KEY, ODIN_LOGO_SWITCH_KEY } from "../../constants";
import EventHelper from "../../network/helpers/event/EventHelper";
import CMSHelper from "../cms/CMSHelper";
import store from "../../../redux/store";
import localForage from "localforage";
import { EventEditionDetails, ICMSApiResponse, IPersonBadge } from "../../types";
import { getStoredColocatedShowsForEvent } from "../../utils/colocatedShowsUtils";

class BadgeImageHelper {
  shouldPrintBadgeLogo = () => {
    return localStorage.getItem(ODIN_LOGO_SWITCH_KEY) === "true";
  };

  private getCachedCmsEventDetails = async (eventEdition: EventEditionDetails): Promise<ICMSApiResponse | null> => {
    const currentEventEdition = store.getState().eventEdition.eventEdition;
    if (currentEventEdition?.gbsCode === eventEdition.gbsCode) {
      return store.getState().eventEdition.cmsEventDetails as ICMSApiResponse | null;
    }

    let cached: Record<string, ICMSApiResponse> = {};
    try {
      cached = (await localForage.getItem<Record<string, ICMSApiResponse>>(CMS_EVENT_DETAILS_STORAGE_KEY)) || {};
      if (cached[eventEdition.gbsCode]) {
        return cached[eventEdition.gbsCode] || null;
      }
    } catch (e) {
      console.warn("[colocated] unable to read cms event cache", e);
    }

    const response = await EventHelper.getBadgeLocation(eventEdition);
    try {
      const updatedCache = { ...cached, [eventEdition.gbsCode]: response.fullResponse };
      await localForage.setItem(CMS_EVENT_DETAILS_STORAGE_KEY, updatedCache);
    } catch (e) {
      console.warn("[colocated] unable to write cms event cache", e);
    }
    return (response.fullResponse as ICMSApiResponse) || null;
  };

  private resolveEventEditionForBadge = async (badge: IPersonBadge, isFreya = false) => {
    const normalizeGbs = (value?: string | null) => (value || "").trim().toUpperCase();
    const currentEventEdition = EventHelper.eventEdition;
    const badgeGbsCode = badge.eventEditionGbsCode;
    const normalizedBadgeGbsCode = normalizeGbs(badgeGbsCode);

    if (normalizedBadgeGbsCode && normalizeGbs(currentEventEdition?.gbsCode) === normalizedBadgeGbsCode) {
      return currentEventEdition;
    }

    if (normalizedBadgeGbsCode && isFreya) {
      const stored = await getStoredColocatedShowsForEvent(currentEventEdition);
      const match = stored.find(show => normalizeGbs(show.gbsCode) === normalizedBadgeGbsCode);
      if (match) {
        return match;
      }
    }

    return currentEventEdition || null;
  };

  getBadgeLogo = async (sku: string) => {
    if (!this.shouldPrintBadgeLogo()) {
      return "";
    }
    const cmsEventDetails = store.getState().eventEdition.cmsEventDetails;
    if (!cmsEventDetails || !EventHelper.eventEdition) {
      return "";
    }
    return CMSHelper.getCmsImageUrl(sku, "logo", EventHelper.eventEdition!, cmsEventDetails);
  };

  getBadgeSideImages = async (sku: string) => {
    if (!this.shouldPrintBadgeLogo()) {
      return ["", "", ""];
    }

    const cmsEventDetails = store.getState().eventEdition.cmsEventDetails;
    if (!cmsEventDetails || !EventHelper.eventEdition) {
      return ["", "", ""];
    }

    return [
      CMSHelper.getCmsImageUrl(sku, "image1", EventHelper.eventEdition!, cmsEventDetails),
      CMSHelper.getCmsImageUrl(sku, "image2", EventHelper.eventEdition!, cmsEventDetails),
      CMSHelper.getCmsImageUrl(sku, "image3", EventHelper.eventEdition!, cmsEventDetails)
    ];
  };

  getBadgeLogoForBadge = async (sku: string, badge: IPersonBadge, isFreya = false) => {
    if (!this.shouldPrintBadgeLogo()) {
      return "";
    }

    const eventEdition = await this.resolveEventEditionForBadge(badge, isFreya);
    if (!eventEdition) {
      return "";
    }

    const cmsEventDetails = await this.getCachedCmsEventDetails(eventEdition);
    if (!cmsEventDetails) {
      return "";
    }
    return CMSHelper.getCmsImageUrl(sku, "logo", eventEdition, cmsEventDetails);
  };

  getBadgeSideImagesForBadge = async (sku: string, badge: IPersonBadge, isFreya = false) => {
    if (!this.shouldPrintBadgeLogo()) {
      return ["", "", ""];
    }

    const eventEdition = await this.resolveEventEditionForBadge(badge, isFreya);
    if (!eventEdition) {
      return ["", "", ""];
    }

    const cmsEventDetails = await this.getCachedCmsEventDetails(eventEdition);
    if (!cmsEventDetails) {
      return ["", "", ""];
    }
    return [
      CMSHelper.getCmsImageUrl(sku, "image1", eventEdition, cmsEventDetails),
      CMSHelper.getCmsImageUrl(sku, "image2", eventEdition, cmsEventDetails),
      CMSHelper.getCmsImageUrl(sku, "image3", eventEdition, cmsEventDetails)
    ];
  };
}

const badgeImageHelper = new BadgeImageHelper();

export default badgeImageHelper;
